import React from 'react';
import Svg, {
  Circle, G, Text as SvgText,
} from 'react-native-svg';
import { View, Text } from 'react-native';
import Colors from '../../constants/Colors';

type ChartDatum = {
  index: number;
  value: number;
  date: string;
  label: string;
};

const SEGMENT_COLORS = [
  Colors.light.hereworksGreen400,
  Colors.light.hereworksYellow500,
  Colors.light.hereworksBlue400,
  Colors.light.hereworksOrange,
  Colors.light.hereworksPurple500,
  Colors.light.hereworksRed300,
  Colors.light.hereworksExtraDarkTeal,
];

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInRadians: number,
) => ({
  x: centerX + radius * Math.cos(angleInRadians),
  y: centerY + radius * Math.sin(angleInRadians),
});

const PieChart = ({ chartData }: { chartData: ChartDatum[] }) => {
  const radius = 50;
  const strokeWidth = 100;
  const outerRadius = radius + strokeWidth / 2;
  const padding = 28;
  const center = outerRadius + padding;
  const labelRadius = outerRadius + 16;

  const total = chartData.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;

  const circumference = 2 * Math.PI * radius;
  let cumulativeValue = 0;
  const segments = chartData.map((item, i) => {
    const valueFraction = item.value / total;
    const segmentLength = valueFraction * circumference;

    const startAngle = (cumulativeValue / total) * 2 * Math.PI;
    const midAngle = startAngle + valueFraction * Math.PI;

    cumulativeValue += item.value;

    const labelPos = polarToCartesian(0, 0, labelRadius, midAngle);
    const textAnchor: 'start' | 'end' = Math.cos(midAngle) >= 0 ? 'start' : 'end';

    return {
      item,
      color: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
      segmentLength,
      startAngle,
      labelPos,
      textAnchor,
    };
  });

  return (
    <View>
      <Svg width={center * 2} height={center * 2}>
        <G transform={`translate(${center} ${center})`}>
          {segments.map((segment) => <Circle
            key={`segment_${segment.item.index}`}
            r={radius}
            stroke={segment.color}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={`${segment.segmentLength} ${circumference - segment.segmentLength}`}
            strokeDashoffset={-(segment.startAngle / (2 * Math.PI)) * circumference}
            strokeLinecap="butt"
          />)}
          {segments.map((segment) => <SvgText
            key={`label_${segment.item.index}`}
            x={segment.labelPos.x}
            y={segment.labelPos.y}
            fontSize={12}
            fill={'#000000'}
            alignmentBaseline="middle"
            textAnchor={segment.textAnchor}
          >
            {segment.item.value}
          </SvgText>)}
        </G>
      </Svg>
      <View style={{ marginTop: 16 }}>
          {chartData.map((item, i) => (
            <View key={item.index} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
              <View
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
                  marginRight: 8,
                }}
              />
              <Text style={{ fontSize: 12, color: '#333' }}>{`${item.label}: ${item.value}`}</Text>
            </View>
          ))}
        </View>
      </View>
  );
};

export default PieChart;
